import { Monitor, Tablet, Smartphone, Settings, PanelLeft, Ruler, UploadCloud, MessageSquarePlus, Info, Trash2, MessageSquareText, Home } from 'lucide-react';
import clsx from 'clsx';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditorStore } from '../../stores/editor-store';
import { useSettingsStore } from '../../stores/settings-store';
import { useMeasurementStore } from '../../stores/measurement-store';
import { ExportMenu } from '../export/ExportMenu';
import { AuthButton } from '../auth/AuthButton';
import { Button } from '../ui/Button';
import { useProjectsStore } from '../../stores/projects-store';
import { useAiActivityStore } from '../../stores/ai-activity-store';
import { FeedbackDialog } from '../feedback/FeedbackDialog';
import { useLanguageStore } from '../../stores/language-store';
import { t } from '../../i18n/translations';
import { GuideDialog } from '../guide/GuideDialog';
import { SaveVersionDialog } from '../projects/SaveVersionDialog';
import { appAlert, appConfirm } from '../../services/app-alerts';
import { VersionCommentDialog } from '../projects/VersionCommentDialog';
import { VersionPicker } from '../projects/VersionPicker';
import { getPageSnapshots } from '../../utils/grapes-page-snapshots';

const devices = [
  { name: 'Desktop' as const, icon: Monitor, titleKey: 'switchToDesktop' as const },
  { name: 'Tablet' as const, icon: Tablet, titleKey: 'switchToTablet' as const },
  { name: 'Mobile' as const, icon: Smartphone, titleKey: 'switchToMobile' as const },
];

export function TopBar() {
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideStepIndex, setGuideStepIndex] = useState(0);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const currentDevice = useEditorStore((s) => s.currentDevice);
  const setDevice = useEditorStore((s) => s.setDevice);
  const toggleSidebar = useSettingsStore((s) => s.toggleSidebar);
  const setSettingsOpen = useSettingsStore((s) => s.setSettingsOpen);
  const measurementMode = useMeasurementStore((s) => s.measurementMode);
  const toggleMeasurementMode = useMeasurementStore((s) => s.toggleMeasurementMode);
  const currentProject = useProjectsStore((s) => s.currentProject);
  const saveCurrentContent = useProjectsStore((s) => s.saveCurrentContent);
  const loadProjectContent = useProjectsStore((s) => s.loadProjectContent);
  const setCurrentProjectVersion = useProjectsStore((s) => s.setCurrentProjectVersion);
  const deleteCurrentProjectVersion = useProjectsStore((s) => s.deleteCurrentProjectVersion);
  const setCurrentProjectVersionComment = useProjectsStore((s) => s.setCurrentProjectVersionComment);
  const startAiActivity = useAiActivityStore((s) => s.start);
  const stopAiActivity = useAiActivityStore((s) => s.stop);
  const language = useLanguageStore((s) => s.language);
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  const loadContent = useEditorStore((s) => s.loadContent);

  const handleSave = async (mode: 'overwrite' | 'new', comment: string) => {
    const editor = useEditorStore.getState().editorInstance;
    if (!editor || !currentProject) return;
    startAiActivity();
    try {
      const html = editor.getHtml() || '';
      const css = editor.getCss() || '';
      const components = editor.getComponents().toJSON();
      const styles = editor.getStyle();
      const projectData = (editor as any).getProjectData ? (editor as any).getProjectData() : undefined;
      const pageSnapshots = getPageSnapshots(editor as any);
      await saveCurrentContent({ html, css, components, styles, projectData, pageSnapshots }, { mode, comment });
    } finally {
      stopAiActivity();
    }
  };

  const meta = currentProject?.contentVersionMeta ?? [];
  const selectedVersionId = currentProject?.activeContentVersionId ?? (meta[meta.length - 1]?.id ?? '');
  const selectedEntry = meta.find((m) => m.id === selectedVersionId) ?? meta[meta.length - 1];
  const selectedComment = selectedEntry?.comment;

  return (
    <div className="flex items-center justify-between px-3 py-2 border-b border-surface-lighter bg-surface-light shrink-0">
      <div className="flex items-center gap-3">
        <button
          onClick={toggleSidebar}
          className="text-text-muted hover:text-text transition-colors"
          title={t(language, 'toggleSidebar')}
        >
          <PanelLeft size={18} />
        </button>
        <button
          onClick={() => navigate('/')}
          className="text-text-muted hover:text-text transition-colors"
          title={language === 'ru' ? 'Кабинет' : 'Cabinet'}
        >
          <Home size={18} />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo.webp" alt="UI Grace" className="w-6 h-6 object-contain" />
          <span className="text-sm font-semibold text-text">UI Grace</span>
        </div>
      </div>

      <div className="flex items-center gap-0.5 bg-surface rounded px-1 py-0.5">
        {devices.map(({ name, icon: Icon, titleKey }) => (
          <button
            key={name}
            onClick={() => setDevice(name)}
            className={clsx(
              'p-1.5 rounded transition-colors',
              currentDevice === name
                ? 'bg-surface-lighter text-accent'
                : 'text-text-muted hover:text-text'
            )}
            title={t(language, titleKey)}
          >
            <Icon size={15} />
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1.5">
        <AuthButton />
        <div className="h-4 w-px bg-surface-lighter" />
        {currentProject && (
          <label className="flex items-center gap-2 rounded border border-surface-lighter bg-surface px-2 py-1">
            <span className="text-xs font-semibold text-text-muted">{t(language, 'version')}</span>
            <VersionPicker
              meta={meta}
              valueId={selectedVersionId}
              onChange={async (nextId) => {
                if (!nextId || nextId === selectedVersionId) return;
                await setCurrentProjectVersion(nextId);
                const content = await loadProjectContent(currentProject.id, { versionId: nextId });
                loadContent(content ?? { html: '', css: '', components: [], styles: [] });
              }}
            />
            <button
              type="button"
              className="ml-0.5 inline-flex h-6 w-6 items-center justify-center rounded text-text-muted hover:text-text hover:bg-surface-lighter transition-colors"
              title={t(language, 'viewComment')}
              onClick={() => setCommentDialogOpen(true)}
              disabled={!currentProject || meta.length === 0}
            >
              <MessageSquareText size={14} />
            </button>
            <button
              type="button"
              className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded text-text-muted hover:text-red-400 hover:bg-surface-lighter transition-colors"
              title={t(language, 'deleteVersionTitle')}
              onClick={async () => {
                if (!currentProject) return;

                try {
                  const confirmed = await appConfirm(t(language, 'deleteVersionPrompt'), {
                    title: t(language, 'deleteVersionTitle'),
                    confirmText: t(language, 'deleteVersion'),
                    cancelText: language === 'ru' ? 'Отмена' : 'Cancel',
                  });
                  if (!confirmed) return;

                  const result = await deleteCurrentProjectVersion(selectedVersionId);
                  if (result?.nextVersionId) {
                    const content = await loadProjectContent(currentProject.id, { versionId: result.nextVersionId });
                    loadContent(content ?? { html: '', css: '', components: [], styles: [] });
                  }
                } catch (e: any) {
                  if (e?.message === 'cannot-delete-last-version') {
                    await appAlert(t(language, 'cannotDeleteLastVersion'), { title: t(language, 'deleteVersionTitle') });
                    return;
                  }
                  console.error(e);
                }
              }}
            >
              <Trash2 size={14} />
            </button>
            <div className="ml-1 h-4 w-px bg-surface-lighter" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSaveDialogOpen(true)}
              title={t(language, 'saveProjectToStorage')}
              disabled={!currentProject}
              className="!h-7 !px-2"
            >
              <UploadCloud size={14} />
              <span>{t(language, 'save')}</span>
            </Button>
          </label>
        )}
        <div className="flex items-center rounded border border-surface-lighter bg-surface p-0.5">
          <button
            type="button"
            onClick={() => setLanguage('ru')}
            className={clsx(
              'h-7 min-w-[36px] rounded px-2 text-xs font-semibold transition-colors',
              language === 'ru'
                ? 'bg-surface-lighter text-text'
                : 'text-text-muted hover:text-text'
            )}
            title={t(language, 'langRu')}
          >
            {t(language, 'langRu')}
          </button>
          <button
            type="button"
            onClick={() => setLanguage('en')}
            className={clsx(
              'h-7 min-w-[36px] rounded px-2 text-xs font-semibold transition-colors',
              language === 'en'
                ? 'bg-surface-lighter text-text'
                : 'text-text-muted hover:text-text'
            )}
            title={t(language, 'langEn')}
          >
            {t(language, 'langEn')}
          </button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleMeasurementMode}
          title={t(language, 'measureDistance')}
          className={clsx(
            measurementMode && '!bg-pink-500 !text-white'
          )}
        >
          <Ruler size={14} />
        </Button>
        <ExportMenu />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFeedbackOpen(true)}
          title={t(language, 'sendInfo')}
        >
          <MessageSquarePlus size={14} />
          <span>{t(language, 'sendInfo')}</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSettingsOpen(true)}
          title={t(language, 'settings')}
        >
          <Settings size={14} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setGuideStepIndex(0);
            setGuideOpen(true);
          }}
          title={language === 'ru' ? 'Гайд' : 'Guide'}
          className="!px-2"
        >
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-surface-lighter text-text-muted">
            <Info size={12} />
          </span>
        </Button>
      </div>
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <GuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        stepIndex={guideStepIndex}
        setStepIndex={setGuideStepIndex}
      />
      <SaveVersionDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onOverwrite={async (comment) => {
          setSaveDialogOpen(false);
          await handleSave('overwrite', comment);
        }}
        onNew={async (comment) => {
          setSaveDialogOpen(false);
          await handleSave('new', comment);
        }}
      />
      <VersionCommentDialog
        open={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        comment={selectedComment}
        onSave={async (next) => {
          if (!selectedVersionId) return;
          await setCurrentProjectVersionComment(selectedVersionId, next);
        }}
      />
    </div>
  );
}
